import config from "@/v2-backoffice-client/config"
import type { CatalogChangesQuery } from "@/v2-backoffice-client/translation/publish/__types__/translation.changes.query"
import type { TranslationPublishRunsQuery } from "@/v2-backoffice-client/translation/publish/__types__/translation.publishRuns.query"

export const PUBLISH_ENVIRONMENTS = ["dev", "prod", "all"] as const
export type PublishEnvironment = (typeof PUBLISH_ENVIRONMENTS)[number]

// Which runtime this backoffice deployment reads and writes. Feature
// configuration, availability and countries are saved runtime-first, so an
// edit made here is already live in THIS environment — the publish only
// carries it to the others.
export const CURRENT_PUBLISH_ENVIRONMENT: Exclude<PublishEnvironment, "all"> =
  config.envType === "prod" ? "prod" : "dev"

export type PublishRun =
  TranslationPublishRunsQuery["getTranslationPublishRuns"]["Runs"][number]

export type CatalogChange =
  CatalogChangesQuery["getCatalogChangesSince"][number]

// Facet values from api.Get_Common_CatalogChangesSince. Text is the only one
// that lives exclusively in the authoring catalog.
export const FACET_TEXT = "text"

export const FACET_LABEL: Record<string, string> = {
  text: "Text",
  configuration: "Configuration",
  availability: "Availability",
  personalData: "Personal data",
  countries: "Countries",
}

// A run is a usable baseline for an environment when it finished successfully,
// shipped something, and reseeded that environment. "all" reseeds both. A run
// dispatched before translation_publish.yml carried its environment in the run
// title reports null, and counts for every environment rather than being
// ignored — treating those as "never published" would show the entire catalog
// as pending the first time this ships.
function isBaselineFor(
  run: PublishRun,
  environment: Exclude<PublishEnvironment, "all">,
): boolean {
  if (run.Status !== "completed" || run.Conclusion !== "success") return false
  if (run.IsDryRun) return false
  return (
    run.Environment == null ||
    run.Environment === environment ||
    run.Environment === "all"
  )
}

/**
 * The run a publish to `environment` should be measured against. Runs arrive
 * newest first, so the first match wins.
 *
 * A publish to "all" reseeds dev and prod, and a value is pending for it when
 * it is pending for either — so its baseline is the OLDER of the two, never
 * the more recent one. Null when that environment has never been published to,
 * which means the next publish ships the catalog's full current state.
 */
export function findPublishBaseline(
  runs: PublishRun[],
  environment: PublishEnvironment,
): PublishRun | null {
  if (environment !== "all") {
    return runs.find((run) => isBaselineFor(run, environment)) ?? null
  }
  const dev = findPublishBaseline(runs, "dev")
  const prod = findPublishBaseline(runs, "prod")
  if (!dev || !prod) return null
  return dev.CreatedAt <= prod.CreatedAt ? dev : prod
}

export type PendingSummary = {
  // Authored in the catalog and nowhere else, so these reach the apps only
  // when a publish ships them.
  textCount: number
  // Configuration, availability, personal data and countries: saved
  // runtime-first, so they are already live in CURRENT_PUBLISH_ENVIRONMENT and
  // travel to the other environments with the publish.
  runtimeCount: number
  total: number
}

export function summarisePendingChanges(
  changes: CatalogChange[],
): PendingSummary {
  const textCount = changes.filter(
    (change) => change.Facet === FACET_TEXT,
  ).length
  return {
    textCount,
    runtimeCount: changes.length - textCount,
    total: changes.length,
  }
}

// Whether a publish to `target` reseeds the runtime this backoffice is looking
// at. Decides how the non-text changes are worded: already live here, or on
// their way somewhere else.
export function targetIncludesCurrentEnvironment(
  target: PublishEnvironment,
): boolean {
  return target === "all" || target === CURRENT_PUBLISH_ENVIRONMENT
}
